/**
 * Removes URL queries and fragments in place from a telemetry envelope before it is sent.
 * Covers URL/URI fields and URLs embedded in messages, stacks, headers, and custom properties.
 * Bare path-relative references require a query/fragment assignment (e.g. callback?code=value)
 * to distinguish them from ordinary prose punctuation such as Ready?yes or issue#123.
 * Keeps hosts, paths, and correlation metadata; never changes the actual browser/network URL.
 * All query parameters are omitted so new credential parameter names are safe by default.
 */
export const sanitizeTelemetryUrls = (telemetry: object): void => {
  const visited = new WeakSet<object>();

  const sanitize = (value: object) => {
    if (visited.has(value)) {
      return;
    }
    visited.add(value);

    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      if (typeof item === "string") {
        (value as Record<string, unknown>)[key] = /(?:url|uri)$/i.test(key)
          ? item.split(/[?#]/, 1)[0]
          : item
              .replace(/((?:https?:\/\/|\/)[^\s"'<>?#]*)(?:[?#][^\s"'<>]*)/gi, "$1")
              .replace(/(^|[\s"'<>])([^\s"'<>?#]+)[?#][^\s"'<>?#=]+=[^\s"'<>]*/g, "$1$2");
      } else if (item && typeof item === "object") {
        sanitize(item);
      }
    }
  };

  sanitize(telemetry);
};
